import { useState, useRef } from 'react';
import { X, Bug, ImagePlus, CheckCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { bugReportsApi } from '../services/api';
import toast from 'react-hot-toast';

interface ReportarBugModalProps {
  open: boolean;
  onClose: () => void;
}

const MAX_IMAGES = 3;
const MAX_IMAGE_SIZE_BYTES = 800 * 1024; // 800 KB

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

export default function ReportarBugModal({ open, onClose }: ReportarBugModalProps) {
  const { t } = useTranslation('common');
  const ck = (s: string, def: string) => t(`chrome.bugReport.${s}`, def);
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showTechInfo, setShowTechInfo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  const handleClose = () => {
    if (loading) return;
    setDescription('');
    setImages([]);
    setSuccess(false);
    setShowTechInfo(false);
    onClose();
  };

  const handleAddImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_IMAGES - images.length;
    const toProcess = files.slice(0, remaining);

    for (const file of toProcess) {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast.error(
          ck('toast.image_too_large', '"{{name}}" excede o limite de 800 KB. Por favor, use uma imagem menor.')
            .replace('{{name}}', file.name)
        );
        continue;
      }
      try {
        const base64 = await toBase64(file);
        setImages(prev => [...prev, base64]);
      } catch {
        toast.error(ck('toast.image_error', 'Erro ao processar a imagem. Tente novamente.'));
      }
    }

    // Limpar input para permitir re-seleção do mesmo arquivo
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await bugReportsApi.create({
        description: description.trim(),
        pageUrl: window.location.pathname,
        userAgent: navigator.userAgent,
        images: images.length > 0 ? images : undefined,
      });
      setSuccess(true);
      setTimeout(() => handleClose(), 6000);
    } catch {
      toast.error(ck('toast.submit_failed', 'Erro ao enviar o reporte. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center">
              <Bug className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-base leading-tight">{ck('title', 'Reportar Bug ou Fale Conosco')}</h2>
              <p className="text-xs text-gray-500">{ck('subtitle', 'Nos ajude a melhorar o sistema')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label={ck('close', 'Fechar')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="overflow-y-auto flex-1">
          {success ? (
            /* Estado de sucesso */
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-9 h-9 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{ck('success.title', 'Reporte enviado!')}</h3>
              <p className="text-gray-500 text-sm">
                {ck('success.body', 'Obrigado! Vamos analisar e resolver o problema o quanto antes.')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-5">

              {/* Descrição */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {ck('label.description', 'Descreva o problema, dúvida ou melhoria')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={ck('placeholder.description', 'Ex: Ao salvar meu palpite na partida X, a página trava e aparece uma tela branca...')}
                  rows={5}
                  maxLength={1000}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={loading}
                />
                <p className="text-right text-xs text-gray-400 mt-1">
                  {description.length}/1000
                </p>
              </div>

              {/* Imagens */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {ck('label.screenshots', 'Prints de tela')}{' '}
                  <span className="text-gray-400 font-normal">
                    {ck('label.screenshots_hint', '(opcional · até {{max}} imagens · máx 800 KB cada)')
                      .replace('{{max}}', String(MAX_IMAGES))}
                  </span>
                </label>

                {images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {images.map((img, i) => (
                      <div key={i} className="relative group aspect-video">
                        <img
                          src={img}
                          alt={ck('screenshot_alt', 'Screenshot {{n}}').replace('{{n}}', String(i + 1))}
                          className="w-full h-full object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(i)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={loading}
                          aria-label={ck('remove_image', 'Remover imagem')}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {images.length < MAX_IMAGES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors w-full justify-center"
                  >
                    <ImagePlus className="w-4 h-4" />
                    {images.length === 0
                      ? ck('add_screenshot', 'Adicionar print de tela')
                      : ck('add_more_screenshots', 'Adicionar mais')}
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleAddImage}
                  disabled={loading}
                />
              </div>

              {/* Info técnica (colapsável) */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowTechInfo(p => !p)}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showTechInfo ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {ck('tech_info.toggle', 'Informações técnicas coletadas automaticamente')}
                </button>
                {showTechInfo && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                    <p className="text-xs text-gray-500">
                      <span className="font-medium text-gray-600">{ck('tech_info.page', 'Página:')}</span>{' '}
                      <span className="font-mono">{window.location.pathname}</span>
                    </p>
                    <p className="text-xs text-gray-500 break-all">
                      <span className="font-medium text-gray-600">{ck('tech_info.browser', 'Navegador:')}</span>{' '}
                      {navigator.userAgent}
                    </p>
                  </div>
                )}
              </div>

              {/* Aviso */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  {ck('privacy_notice', 'Seu nome e e-mail serão incluídos no reporte para facilitar o suporte.')}
                </p>
              </div>

              {/* Botão submit */}
              <button
                type="submit"
                disabled={loading || !description.trim()}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-300 disabled:to-gray-300 text-white px-6 py-3.5 rounded-xl text-sm font-bold transition-all shadow-md disabled:shadow-none disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {ck('sending', 'Enviando...')}
                  </>
                ) : (
                  <>
                    <Bug className="w-4 h-4" />
                    {ck('submit', 'Enviar Reporte')}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
